'use client'

import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useI18n } from '../../components/I18nProvider'
import { useAnalysisStore } from '../../lib/store'
import { getDashboardData, type Image, deleteImage, getImageTriggers, getLatestImageTriggers } from '../../api/imageApi'
import type { Trigger } from '../../lib/dataUtils'

// 替代Next.js Image组件的简单img组件
const NextImage = ({ src, alt, width, height, className }: {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  className?: string;
}) => {
  return (
    <img
      src={src}
      alt={alt}
      width={width}
      height={height}
      className={className}
    />
  );
};

interface DashboardImage {
  id: string
  url: string
  keyword: string
  timestamp: string
  triggers: Trigger[]
  emo_intensity_before?: number // 焦虑程度 (0-10)
  emo_intensity_after?: number // 分析后的焦虑程度 (0-10)
  // 用户评价数据
  relatability_after?: number
  helpfulness_after?: number
  learnability_after?: number
  comment_after?: string
  best_suggestion?: string
  // 分析数据
  analysis?: any
  triggers_list?: any[]
  suggestions?: any
  suggestion_ratings?: any
  feedback?: {
    accuracy?: boolean
    clarity?: boolean
    novelty?: boolean
    noveltyDetail?: string
  }
  rawAnalysisResult?: any
}

// 详情弹窗的数据接口 - 扩展Image类型以包含分别的triggers数据
interface ImageDetail extends Image {
  aiTriggers?: Trigger[]; // AI分析提供的triggers
  userConfirmedTriggers?: Trigger[]; // 用户确认的triggers
}

const Dashboard: React.FC = () => {
  const { t } = useI18n()
  const { isLoggedIn, initializeAuth } = useAnalysisStore()
  const [images, setImages] = useState<DashboardImage[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedImages, setSelectedImages] = useState<Set<string>>(new Set())
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('newest')
  const [selectedImage, setSelectedImage] = useState<DashboardImage | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [showPreview, setShowPreview] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [imageToDelete, setImageToDelete] = useState<string | null>(null)
  
  // 详情弹窗相关状态
  const [showDetail, setShowDetail] = useState(false)
  const [detailImage, setDetailImage] = useState<ImageDetail | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [showImagePreview, setShowImagePreview] = useState(false)

  // 获取历史记录 - 只从后端数据库获取
  useEffect(() => {
    const loadImages = async () => {
      if (!isLoggedIn) {
        setLoading(false)
        return
      }

      try {
        setError(null)
        console.log('[Dashboard] Loading data from database...')
        
        const dashboardData = await getDashboardData()
        
        // 处理用户信息
        if (dashboardData.userInfo) {
          console.log('[Dashboard] User info loaded from database:', {
            userId: dashboardData.userInfo.user_id,
            status: dashboardData.userInfo.status,
            quota: dashboardData.userInfo.quota
          })
        }
        
        // 处理图片数据
        if (dashboardData.images && dashboardData.images.length > 0) {
          console.log(`[Dashboard] Loaded ${dashboardData.images.length} images from database`)
          
          // Convert backend Image[] to DashboardImage[]
          const processedImages = dashboardData.images.map((image: Image) => {
            // 确保处理triggers数据的向后兼容性
            const processedTriggers = image.triggers?.map(t => ({
              id: t.trigger_id || t.id || '',
              title: t.title || '',
              explanation: t.explanation || '',
              impact: t.type || 'general',
              rating: (t as any).rating || null,
              isAccepted: (t as any).isAccepted || false,
              confidence: (t as any).confidence || 1.0,
              name: t.title || '', // Backward compatibility
              type: t.type || 'general'
            })) || [];

            return {
              id: image.image_id,
              url: image.url || image.s3_location || '',
              keyword: image.keyword || 'Unknown',
              timestamp: image.upload_date,
              triggers: processedTriggers,
              emo_intensity_before: image.emo_intensity_before,
              emo_intensity_after: image.emo_intensity_after,
              // 添加用户评价数据
              relatability_after: image.relatability_after,
              helpfulness_after: image.helpfulness_after,
              learnability_after: image.learnability_after,
              comment_after: image.comment_after,
              best_suggestion: image.best_suggestion,
              // 添加分析数据
              analysis: image.analysis,
              triggers_list: image.triggers_list,
              suggestions: image.suggestions,
              suggestion_ratings: image.suggestion_ratings,
              feedback: {
                accuracy: false,
                clarity: false,
                novelty: false,
                noveltyDetail: ''
              },
              rawAnalysisResult: image.analysis_text
            }
          })
          
          setImages(processedImages)
          console.log(`[Dashboard] Successfully loaded ${processedImages.length} images from database`)
        } else {
          setImages([])
          console.log('[Dashboard] No images found in database')
        }

      } catch (err) {
        console.error('[Dashboard] Error loading images from database:', err)
        setError(t('dashboard.error.loadFailed'))
        setImages([])
      } finally {
        setLoading(false)
      }
    }

    loadImages()
  }, [isLoggedIn, t])

  // 确保认证状态已初始化
  useEffect(() => {
    initializeAuth()
  }, [initializeAuth])

  // 排序逻辑
  const sortedImages = [...images].sort((a, b) => {
    if (sortOrder === 'newest') {
      return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    } else {
      return new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    }
  })

  // 处理排序切换
  const handleSortToggle = () => {
    setSortOrder(sortOrder === 'newest' ? 'oldest' : 'newest')
  }

  // 处理图片选择
  const handleImageSelect = (imageId: string) => {
    const newSelected = new Set(selectedImages)
    if (newSelected.has(imageId)) {
      newSelected.delete(imageId)
    } else {
      newSelected.add(imageId)
    }
    setSelectedImages(newSelected)
  }

  // 处理全选/取消全选
  const handleSelectAll = () => {
    if (selectedImages.size === images.length) {
      setSelectedImages(new Set())
    } else {
      setSelectedImages(new Set(images.map(img => img.id)))
    }
  }

  // 处理删除选中的图片 - 只操作数据库
  const handleDeleteSelected = async () => {
    if (selectedImages.size === 0) return
    
    setDeleting(true)
    try {
      const imagesToDelete = Array.from(selectedImages)
      console.log(`[Dashboard] Starting deletion of ${imagesToDelete.length} images from database...`)
      
      // 并行删除所有选中的图片
      const deletePromises = imagesToDelete.map(async (imageId) => {
        try {
          await deleteImage(imageId)
          console.log(`[Dashboard] Successfully deleted image ${imageId} from database`)
          return { imageId, success: true }
        } catch (error) {
          console.error(`[Dashboard] Failed to delete image ${imageId} from database:`, error)
          return { imageId, success: false, error }
        }
      })

      const deleteResults = await Promise.allSettled(deletePromises)
      
      // 统计删除结果
      const successfulDeletes = deleteResults
        .filter(result => result.status === 'fulfilled' && result.value.success)
        .map(result => (result as PromiseFulfilledResult<any>).value.imageId)
      
      const failedDeletes = deleteResults
        .filter(result => result.status === 'rejected' || (result.status === 'fulfilled' && !result.value.success))
      
      // 更新UI状态 - 只移除成功删除的图片
      if (successfulDeletes.length > 0) {
        setImages(prevImages => {
          const newImages = prevImages.filter(img => !successfulDeletes.includes(img.id))
          console.log(`[Dashboard] Updated images state: removed ${successfulDeletes.length} images, remaining: ${newImages.length}`)
          return newImages
        })
        
        // 清除已删除图片的选中状态
        setSelectedImages(prevSelected => {
          const newSelected = new Set(prevSelected)
          successfulDeletes.forEach(imageId => newSelected.delete(imageId))
          console.log(`[Dashboard] Updated selected images: removed ${successfulDeletes.length} images`)
          return newSelected
        })
      }
      
      // 显示删除结果
      if (successfulDeletes.length > 0 && failedDeletes.length === 0) {
        console.log(`[Dashboard] Successfully deleted ${successfulDeletes.length} images from database`)
      } else if (successfulDeletes.length > 0 && failedDeletes.length > 0) {
        console.warn(`[Dashboard] Deleted ${successfulDeletes.length} images, ${failedDeletes.length} failed`)
        setError(`已删除 ${successfulDeletes.length} 张图片，${failedDeletes.length} 张删除失败`)
      } else {
        console.error(`[Dashboard] Failed to delete all ${failedDeletes.length} images`)
        setError('删除失败，请稍后重试')
      }
      
    } catch (err) {
      console.error('[Dashboard] Error during deletion:', err)
      setError(t('dashboard.error.deleteFailed'))
    } finally {
      setDeleting(false)
    }
  }

  // 处理单个图片删除 - 只操作数据库
  const handleDeleteSingle = async (imageId: string) => {
    // 显示确认弹框
    setImageToDelete(imageId)
    setShowDeleteConfirm(true)
  }

  const confirmDelete = async () => {
    if (!imageToDelete) return
    
    setDeleting(true)
    setShowDeleteConfirm(false)
    
    try {
      console.log(`[Dashboard] Starting deletion of single image: ${imageToDelete}`)
      
      await deleteImage(imageToDelete)
      console.log(`[Dashboard] Successfully deleted image ${imageToDelete} from database`)
      
      // 更新UI状态 - 确保删除成功后才更新
      setImages(prevImages => {
        const newImages = prevImages.filter(img => img.id !== imageToDelete)
        console.log(`[Dashboard] Updated images state: removed ${imageToDelete}, remaining: ${newImages.length}`)
        return newImages
      })
      
      // 如果删除的图片在选中列表中，也要从选中列表移除
      setSelectedImages(prevSelected => {
        const newSelected = new Set(prevSelected)
        newSelected.delete(imageToDelete)
        console.log(`[Dashboard] Updated selected images: removed ${imageToDelete}`)
        return newSelected
      })
      
      // 清除错误状态
      setError(null)
      
    } catch (err) {
      console.error('[Dashboard] Error during single image deletion:', err)
      setError(t('dashboard.error.deleteFailed'))
      // 删除失败时，不更新images状态，保持原状
    } finally {
      setDeleting(false)
      setImageToDelete(null)
      // 强制重新渲染以确保UI状态正确
      console.log(`[Dashboard] Delete operation completed. Current images count: ${images.length}`)
    }
  }

  const cancelDelete = () => {
    setShowDeleteConfirm(false)
    setImageToDelete(null)
  }

  const handleImageClick = (image: DashboardImage) => {
    setSelectedImage(image)
    setShowPreview(true)
  }

  // 处理查看详情
  const handleViewDetail = async (imageId: string) => {
    setDetailLoading(true)
    try {
      // 从已有的图片数据中找到对应的图片
      const existingImage = images.find(img => img.id === imageId);
      if (!existingImage) {
        throw new Error('Image not found in existing data');
      }

      console.log('[Dashboard] Found existing image data:', existingImage);

      // 只调用triggers相关的API接口
      const [aiTriggersResult, userConfirmedTriggersResult] = await Promise.allSettled([
        getImageTriggers(imageId),
        getLatestImageTriggers(imageId)
      ]);

      console.log('[Dashboard] AI triggers result:', aiTriggersResult);
      console.log('[Dashboard] User confirmed triggers result:', userConfirmedTriggersResult);

      // 处理triggers结果
      const aiTriggers = aiTriggersResult.status === 'fulfilled' ? aiTriggersResult.value.triggers : [];
      const userConfirmedTriggers = userConfirmedTriggersResult.status === 'fulfilled' ? userConfirmedTriggersResult.value.triggers : [];

      // 构建详情数据，使用已有的图片数据
      const enhancedDetail: ImageDetail = {
        image_id: existingImage.id,
        upload_date: existingImage.timestamp,
        s3_location: existingImage.url,
        url: existingImage.url,
        keyword: existingImage.keyword,
        emo_intensity_before: existingImage.emo_intensity_before,
        emo_intensity_after: existingImage.emo_intensity_after,
        // 从existingImage中提取用户评价数据
        relatability_after: existingImage.relatability_after,
        helpfulness_after: existingImage.helpfulness_after,
        learnability_after: existingImage.learnability_after,
        comment_after: existingImage.comment_after,
        best_suggestion: existingImage.best_suggestion,
        // 从existingImage中提取分析数据
        analysis: existingImage.analysis,
        analysis_text: existingImage.rawAnalysisResult,
        // 从API获取的triggers数据
        aiTriggers: aiTriggers as any,
        userConfirmedTriggers: userConfirmedTriggers as any,
        // 其他可能的数据
        triggers: existingImage.triggers as any,
        triggers_list: existingImage.triggers_list,
        suggestions: existingImage.suggestions,
        suggestion_ratings: existingImage.suggestion_ratings
      };

      console.log('[Dashboard] Enhanced detail data:', enhancedDetail);
      setDetailImage(enhancedDetail);
      setShowDetail(true);
    } catch (err) {
      console.error('[Dashboard] Error preparing image detail:', err);
      setError(t('dashboard.error.detailFailed'));
    } finally {
      setDetailLoading(false);
    }
  }

  const handleCloseDetail = () => {
    setShowDetail(false)
    setDetailImage(null)
    setShowImagePreview(false)
  }


  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString()
  }

  const getRatingDescription = (rating: number) => {
    switch (rating) {
      case 2: return '非常不同意';
      case 4: return '不同意';
      case 3: return '中立';
      case 8: return '同意';
      case 10: return '非常同意';
      default: return '未知';
    }
  };

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <h1 className="text-xl font-semibold text-gray-900 mb-4">
            {t('dashboard.loginRequired')}
          </h1>
          <Link
            to="/"
            className="inline-flex items-center px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
          >
            {t('dashboard.backToHome')}
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Link
                to="/"
                className="p-2 text-gray-600 hover:text-teal-600 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                </svg>
              </Link>
              <div className="flex flex-col">
                <h1 className="text-lg font-semibold text-gray-900">
                  {t('dashboard.title')}
                </h1>
              </div>
            </div>
            <button
              onClick={handleSortToggle}
              className="p-2 text-gray-600 hover:text-teal-600 transition-colors"
              title={sortOrder === 'newest' ? t('dashboard.sort.descending') : t('dashboard.sort.ascending')}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 pb-20">
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
          </div>
        ) : images.length === 0 ? (
          <div className="text-center py-12">
            <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <p className="text-gray-500 mb-4">{t('dashboard.noImages')}</p>
            <Link
              to="/"
              className="inline-flex items-center px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
            >
              {t('dashboard.startAnalysis')}
            </Link>
          </div>
        ) : (
          <>
            {/* Stats */}
            <div className="mb-4 p-3 bg-white rounded-lg border border-gray-200">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">
                    {t('dashboard.totalImages')}: 
                  </span>
                  <span className="font-semibold text-gray-900 ml-1">
                    {images.length}
                  </span>
                </div>
               
              </div>
            </div>

            {/* Bulk Actions */}
            {selectedImages.size > 0 && (
              <div className="mb-4 p-3 bg-teal-50 border border-teal-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-teal-700">
                    {t('dashboard.selectedCount').replace('{count}', selectedImages.size.toString())}
                  </span>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setSelectedImages(new Set())}
                      className="px-3 py-1 text-xs text-teal-600 hover:text-teal-700"
                    >
                      {t('dashboard.clearSelection')}
                    </button>
                    <button
                      onClick={handleDeleteSelected}
                      disabled={deleting}
                      className="px-3 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                    >
                      {deleting ? t('dashboard.deleting') : t('dashboard.deleteSelected')}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Images Grid */}
            <div className="space-y-3">
              {sortedImages.map((image) => (
                <div
                  key={image.id}
                  className="bg-white border border-gray-200 rounded-lg overflow-hidden cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => handleViewDetail(image.id)}
                >
                  <div className="p-3">
                    <div className="flex items-start space-x-3">
                      {/* Selection checkbox */}
                      <div className="flex-shrink-0 pt-1" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedImages.has(image.id)}
                          onChange={() => handleImageSelect(image.id)}
                          className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 bg-white !bg-white dark:!bg-white"
                        />
                      </div>

                      {/* Image thumbnail */}
                      <div className="flex-shrink-0">
                        <div
                          onClick={(e) => {
                            e.stopPropagation();
                            handleImageClick(image);
                          }}
                          className="w-16 h-16 bg-gray-100 rounded-lg overflow-hidden cursor-pointer hover:opacity-80 transition-opacity"
                        >
                          {image.url ? (
                            <NextImage
                              src={image.url}
                              alt={image.keyword || 'Image'}
                              width={64}
                              height={64}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0 pr-2">
                            <h3 className="text-sm font-medium text-gray-900 break-words leading-tight">
                              {image.keyword || t('dashboard.noKeyword')}
                            </h3>
                            <p className="text-xs text-gray-500 mt-1">
                              {formatDate(image.timestamp)}
                            </p>
                          </div>
                          
                          {/* Actions */}
                          <div className="flex items-center space-x-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                            <button
                              onClick={() => handleViewDetail(image.id)}
                              disabled={detailLoading}
                              className="p-1.5 text-gray-400 hover:text-blue-600 transition-colors disabled:opacity-50"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleDeleteSingle(image.id)}
                              disabled={deleting}
                              className="p-1.5 text-gray-400 hover:text-red-600 transition-colors disabled:opacity-50"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Bottom Action Bar */}
      {images.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <button
              onClick={handleSelectAll}
              className="text-sm text-teal-600 hover:text-teal-700"
            >
              {selectedImages.size === images.length ? t('dashboard.deselectAll') : t('dashboard.selectAll')}
            </button>
           
          </div>
        </div>
      )}

      {/* Image Preview Modal */}
      {showPreview && selectedImage && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
          onClick={() => setShowPreview(false)} // 点击背景关闭
        >
          {/* 图片容器 */}
          <div 
            className="relative max-w-full max-h-full"
            onClick={(e) => e.stopPropagation()} // 防止点击图片时关闭
          >
            {/* 关闭按钮 - 缩小并放置在图片上方 */}
            <button
              onClick={() => setShowPreview(false)}
              className="absolute -top-2 -right-2 text-white hover:text-gray-300 bg-black bg-opacity-80 rounded-full p-1.5 transition-colors z-60 shadow-lg"
              title="关闭"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            {selectedImage.url && (
              <NextImage
                src={selectedImage.url}
                alt={selectedImage.keyword || 'Preview'}
                width={800}
                height={600}
                className="max-w-full max-h-full object-contain rounded-lg"
              />
            )}
          </div>
        </div>
      )}

      {/* Image Detail Modal */}
      {showDetail && detailImage && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                {detailImage.keyword || '图片详情'}
              </h3>
              <button onClick={handleCloseDetail} className="text-gray-600 hover:text-gray-800">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
              {/* 图片显示 */}
              {detailImage.url && (
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">图片</h4>
                  <div className="flex justify-center">
                    <img
                      src={detailImage.url}
                      alt={detailImage.keyword || 'Image'}
                      className="max-w-full max-h-64 object-contain rounded-lg border border-gray-200 cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={() => setShowImagePreview(true)}
                      title="点击放大查看"
                    />
                  </div>
                </div>
              )}

              {/* 基本信息 */}
              <div className="mb-6">
                <h4 className="text-sm font-medium text-gray-900 mb-3">基本信息</h4>
                <div className="space-y-3 text-xs">
                  <div>
                    <span className="text-gray-500">主题/关键词:</span>
                    <span className="ml-2 text-gray-900">{detailImage.keyword || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">上传日期:</span>
                    <span className="ml-2 text-gray-900">{formatDate(detailImage.upload_date)}</span>
                  </div>
                </div>
              </div>

              {/* 情感评估 */}
              <div className="mb-6">
                <h4 className="text-sm font-medium text-gray-900 mb-3">情感评估</h4>
                <div className="space-y-4">

                  {/* 焦虑程度变化 */}
                  {detailImage.emo_intensity_before && detailImage.emo_intensity_after && (
                    <div className="bg-blue-50 p-3 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-medium text-blue-900">焦虑程度变化</span>
                        <span className={`text-xs font-medium ${
                          detailImage.emo_intensity_after < detailImage.emo_intensity_before ? 'text-green-600' :
                          detailImage.emo_intensity_after > detailImage.emo_intensity_before ? 'text-red-600' : 'text-gray-600'
                        }`}>
                          {detailImage.emo_intensity_after < detailImage.emo_intensity_before ? '↓ 改善' :
                           detailImage.emo_intensity_after > detailImage.emo_intensity_before ? '↑ 加重' : '→ 无变化'}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="flex-1 bg-gray-200 rounded-full h-1">
                          <div 
                            className="bg-blue-500 h-1 rounded-full"
                            style={{ width: `${((detailImage.emo_intensity_before) / 10) * 100}%` }}
                          ></div>
                        </div>
                        <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                        </svg>
                        <div className="flex-1 bg-gray-200 rounded-full h-1">
                          <div 
                            className={`h-1 rounded-full ${
                              detailImage.emo_intensity_after < detailImage.emo_intensity_before ? 'bg-green-500' :
                              detailImage.emo_intensity_after > detailImage.emo_intensity_before ? 'bg-red-500' : 'bg-blue-500'
                            }`}
                            style={{ width: `${((detailImage.emo_intensity_after) / 10) * 100}%` }}
                          ></div>
                        </div>
                      </div>
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>分析前: {detailImage.emo_intensity_before}</span>
                        <span>分析后: {detailImage.emo_intensity_after}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* 用户评价 */}
              <div className="mb-6">
                <h4 className="text-sm font-medium text-gray-900 mb-3">用户评价</h4>
                <div className="grid grid-cols-1 gap-2 text-xs">
                  <div>
                    <span className="text-gray-500">分析结果帮助我更好地理解我的焦虑:</span>
                    <span className="ml-2 text-gray-900">
                      {detailImage.relatability_after ? getRatingDescription(detailImage.relatability_after) : 'N/A'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">系统提供的建议对缓解焦虑有帮助:</span>
                    <span className="ml-2 text-gray-900">
                      {detailImage.helpfulness_after ? getRatingDescription(detailImage.helpfulness_after) : 'N/A'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">我学会了如何应对这类焦虑内容:</span>
                    <span className="ml-2 text-gray-900">
                      {detailImage.learnability_after ? getRatingDescription(detailImage.learnability_after) : 'N/A'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">最佳建议类型:</span>
                    <span className="ml-2 text-gray-900">
                      {detailImage.best_suggestion === 'cognitive_reappraisal' ? '换种新看法' :
                       detailImage.best_suggestion === 'problem_focus_coping' ? '实用行动' :
                       detailImage.best_suggestion === 'media_regulation' ? '管好你的信息流' : 
                       detailImage.best_suggestion || 'N/A'}
                    </span>
                  </div>
                  {detailImage.comment_after && (
                    <div>
                      <span className="text-gray-500">用户评论:</span>
                      <span className="ml-2 text-gray-900">{detailImage.comment_after}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* AI分析结果 */}
              {detailImage.analysis && (
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">AI分析结果</h4>
                  <div className="text-xs">
                    <div className="mb-2">
                      <span className="text-gray-500">分析关键词:</span>
                      <span className="ml-2 text-gray-900">{detailImage.analysis.keyword || 'N/A'}</span>
                    </div>
                    {detailImage.analysis.triggers && detailImage.analysis.triggers.length > 0 && (
                      <div>
                        <span className="text-gray-500">AI识别到的焦虑触发因素:</span>
                        <div className="mt-1 space-y-1">
                          {detailImage.analysis.triggers.map((trigger, index) => (
                            <div key={index} className="ml-4 text-gray-700">
                              <span className="font-medium">{trigger.title}:</span>
                              <span className="ml-1">{trigger.explanation}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* AI分析提供的Triggers */}
              {detailImage.aiTriggers && detailImage.aiTriggers.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">AI分析提供的焦虑触发因素</h4>
                  <div className="space-y-2">
                    {detailImage.aiTriggers.map((trigger, index) => (
                      <div key={index} className="text-xs border-l-2 border-blue-200 pl-3">
                        <div className="font-medium text-gray-900">{trigger.title}</div>
                        <div className="text-gray-600 mt-1">{trigger.explanation}</div>
                        <div className="flex items-center space-x-2 mt-1">
                          {(trigger as any).user_created && (
                            <span className="text-xs bg-purple-100 text-purple-800 px-1 py-0.5 rounded">
                              用户创建
                            </span>
                          )}
                          {(trigger as any).is_reported && (
                            <span className="text-xs bg-red-100 text-red-800 px-1 py-0.5 rounded">
                              已举报
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* 用户确认的Triggers */}
              {detailImage.userConfirmedTriggers && detailImage.userConfirmedTriggers.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">用户确认的焦虑触发因素</h4>
                  <div className="space-y-2">
                    {detailImage.userConfirmedTriggers.map((trigger, index) => (
                      <div key={index} className="text-xs border-l-2 border-yellow-200 pl-3">
                        <div className="font-medium text-gray-900">{trigger.title}</div>
                        <div className="text-gray-600 mt-1">{trigger.explanation}</div>
                        <div className="flex items-center space-x-2 mt-1">
                          {(trigger as any).user_created && (
                            <span className="text-xs bg-purple-100 text-purple-800 px-1 py-0.5 rounded">
                              用户创建
                            </span>
                          )}
                          {(trigger as any).is_reported && (
                            <span className="text-xs bg-red-100 text-red-800 px-1 py-0.5 rounded">
                              已举报
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* AI建议 */}
              {detailImage.suggestions && (
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">AI建议</h4>
                  <div className="space-y-4">
                    {detailImage.suggestions.cognitive_reappraisal && (
                      <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                        <div className="flex items-center justify-between mb-2">
                          <div className="font-medium text-gray-900 text-sm">换种新看法</div>
                          {detailImage.suggestion_ratings?.cognitive_reappraisal && (
                            <div className="flex items-center space-x-2">
                              <div className="flex items-center">
                                {[...Array(5)].map((_, i) => (
                                  <svg
                                    key={i}
                                    className={`w-4 h-4 ${i < detailImage.suggestion_ratings!.cognitive_reappraisal! ? 'text-yellow-400' : 'text-gray-300'}`}
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                  >
                                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                  </svg>
                                ))}
                              </div>
                              <span className="text-sm font-medium text-purple-700 bg-purple-100 px-2 py-1 rounded">
                                {detailImage.suggestion_ratings.cognitive_reappraisal}/5
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="text-gray-700 text-sm leading-relaxed">{detailImage.suggestions.cognitive_reappraisal}</div>
                      </div>
                    )}
                    {detailImage.suggestions.problem_focus_coping && (
                      <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                        <div className="flex items-center justify-between mb-2">
                          <div className="font-medium text-gray-900 text-sm">实用行动</div>
                          {detailImage.suggestion_ratings?.problem_focus_coping && (
                            <div className="flex items-center space-x-2">
                              <div className="flex items-center">
                                {[...Array(5)].map((_, i) => (
                                  <svg
                                    key={i}
                                    className={`w-4 h-4 ${i < detailImage.suggestion_ratings!.problem_focus_coping! ? 'text-yellow-400' : 'text-gray-300'}`}
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                  >
                                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                  </svg>
                                ))}
                              </div>
                              <span className="text-sm font-medium text-blue-700 bg-blue-100 px-2 py-1 rounded">
                                {detailImage.suggestion_ratings.problem_focus_coping}/5
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="text-gray-700 text-sm leading-relaxed">{detailImage.suggestions.problem_focus_coping}</div>
                      </div>
                    )}
                    {detailImage.suggestions.media_regulation && (
                      <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                        <div className="flex items-center justify-between mb-2">
                          <div className="font-medium text-gray-900 text-sm">管好你的信息流</div>
                          {detailImage.suggestion_ratings?.media_regulation && (
                            <div className="flex items-center space-x-2">
                              <div className="flex items-center">
                                {[...Array(5)].map((_, i) => (
                                  <svg
                                    key={i}
                                    className={`w-4 h-4 ${i < detailImage.suggestion_ratings!.media_regulation! ? 'text-yellow-400' : 'text-gray-300'}`}
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                  >
                                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                  </svg>
                                ))}
                              </div>
                              <span className="text-sm font-medium text-green-700 bg-green-100 px-2 py-1 rounded">
                                {detailImage.suggestion_ratings.media_regulation}/5
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="text-gray-700 text-sm leading-relaxed">{detailImage.suggestions.media_regulation}</div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* 调试信息 - 开发时使用 */}
              {process.env.NODE_ENV === 'development' && (
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">调试信息</h4>
                  <details className="text-xs">
                    <summary className="cursor-pointer text-gray-600">查看原始数据</summary>
                    <pre className="mt-2 p-2 bg-gray-100 rounded border overflow-auto max-h-40 text-xs">
                      {JSON.stringify(detailImage, null, 2)}
                    </pre>
                  </details>
                  <details className="text-xs mt-2">
                    <summary className="cursor-pointer text-gray-600">查看用户评价数据</summary>
                    <div className="mt-2 p-2 bg-gray-100 rounded border text-xs">
                      <p><strong>relatability_after:</strong> {detailImage.relatability_after} (类型: {typeof detailImage.relatability_after})</p>
                      <p><strong>helpfulness_after:</strong> {detailImage.helpfulness_after} (类型: {typeof detailImage.helpfulness_after})</p>
                      <p><strong>learnability_after:</strong> {detailImage.learnability_after} (类型: {typeof detailImage.learnability_after})</p>
                      <p><strong>comment_after:</strong> {detailImage.comment_after} (类型: {typeof detailImage.comment_after})</p>
                      <p><strong>best_suggestion:</strong> {detailImage.best_suggestion} (类型: {typeof detailImage.best_suggestion})</p>
                      <p><strong>emo_intensity_before:</strong> {detailImage.emo_intensity_before} (类型: {typeof detailImage.emo_intensity_before})</p>
                      <p><strong>emo_intensity_after:</strong> {detailImage.emo_intensity_after} (类型: {typeof detailImage.emo_intensity_after})</p>
                    </div>
                  </details>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 图片放大预览模态框 */}
      {showImagePreview && detailImage?.url && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-[60] p-4"
          onClick={() => setShowImagePreview(false)}
        >
          {/* 图片容器 */}
          <div 
            className="relative max-w-full max-h-full"
            onClick={(e) => e.stopPropagation()}
          >
            {/* 关闭按钮 */}
            <button
              onClick={() => setShowImagePreview(false)}
              className="absolute -top-2 -right-2 text-white hover:text-gray-300 bg-black bg-opacity-80 rounded-full p-1.5 transition-colors z-70 shadow-lg"
              title="关闭"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            <img
              src={detailImage.url}
              alt={detailImage.keyword || 'Preview'}
              className="max-w-full max-h-full object-contain rounded-lg"
            />
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-gray-900">
                  {t('dashboard.deleteConfirm.title')}
                </h3>
              </div>
            </div>
            <div className="mb-6">
              <p className="text-sm text-gray-600">
                {t('dashboard.deleteConfirm.description')}
              </p>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={cancelDelete}
                disabled={deleting}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50"
              >
                {t('dashboard.deleteConfirm.cancel')}
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleting}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
              >
                {deleting ? t('dashboard.deleteConfirm.deleting') : t('dashboard.deleteConfirm.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Dashboard 